// 모사 축 **행 편집기** — 축 하나의 위젯(radio/select/자유 입력/checkbox) + 해제(✕)를 그린다.
//
// PreviewContextBar(실데이터 반영 모달)의 모사 섹션에서 추출했다(2026-09-03) — 전역 속성 패널의
// 「모사 데이터」 프리셋 편집기가 같은 행을 그려야 해서다. 두 호스트가 각자 그리면 위젯 어휘가
// 갈라진다(같은 축이 모달에선 select, 패널에선 text가 되는 식).
//
// ⚠️ 이 컴포넌트는 축이 무엇을 뜻하는지 **모른다** — label/widget/options/unsetLabel만 읽는다
// (PreviewContextBar 규약 그대로). 값 파싱·라벨 보관은 호스트 몫이고, 자유 입력 파싱 규칙은
// `parseSimulationAxisInput`(previewSimulationAxes.ts)을 공유한다.

import type { PreviewSimulationAxis, PreviewSimulationOption } from '@safetyhub/shared';
import { formatDateTimeISO } from '@safetyhub/shared';

/**
 * select가 가리킬 선택지 index — **값 일치가 먼저, 라벨은 폴백**이다.
 *
 * 값은 opaque라 재조회하면 같은 것이라도 **다른 객체**가 되어 참조 비교가 어긋난다. 그래서
 * 고른 순간 보관해 둔 라벨로 되찾는 2차 경로가 있는데, 이 둘을 한 술어에 OR로 묶으면
 * (`o.value === current || storedLabel === o.label`) **라벨이 먼저 걸리는 선택지가 값 일치를
 * 앞지른다** — 라벨이 같고 값이 다른 선택지가 목록에 있으면 엉뚱한 항목이 선택돼 보인다.
 * 두 단계로 나누면 값이 살아 있는 흔한 경우가 항상 정확히 이긴다.
 *
 * 라벨만 있고 값이 없으면 `-1`이다 — 고르지 않은 축을 고른 것처럼 보이면 안 된다.
 */
export function resolveSelectIndex(
  options: readonly { label: string; value: unknown }[] | undefined,
  current: unknown,
  storedLabel: string | undefined,
): number {
  if (!options || current === undefined) return -1;
  const byValue = options.findIndex((o) => o.value === current);
  if (byValue >= 0) return byValue;
  if (storedLabel === undefined) return -1;
  return options.findIndex((o) => o.label === storedLabel);
}

interface Props {
  axis: PreviewSimulationAxis;
  /** 현재 실린 값(undefined = 미지정) */
  current: unknown;
  /**
   * 선택형 축의 선택지. 없으면 값+보관 라벨로 **한 줄을 합성**한다 — `plan` 축은 모달을 다시
   * 열면 조회 결과가 비어 있는데(재조회는 부수효과라 금지), 값·라벨은 남아 있으므로 그 둘로
   * 현재 선택을 보여 준다(없으면 select가 -1로 떨어져 "통합(전체)"을 고른 것처럼 **화면만
   * 거짓말**한다 — 2026-09-02 dev 실측).
   */
  options?: PreviewSimulationOption[];
  /** 고른 순간 보관한 라벨(opaque 값 이름 복원 + date 입력 원문 유지) */
  storedLabel?: string;
  loading?: boolean;
  error?: string;
  disabled?: boolean;
  /** radio 묶음 name의 호스트별 접두 — 두 호스트(모달·패널)가 동시에 떠도 묶음이 섞이지 않게. */
  radioNamePrefix: string;
  /** 선택형(radio/select/checkbox) 값 선택 — undefined면 해제 */
  onSelectOption: (option: PreviewSimulationOption | undefined) => void;
  /** 자유 입력(text/number/date) 원문 — 파싱은 호스트가 `parseSimulationAxisInput`으로 */
  onTypeValue: (raw: string) => void;
  /** 행 제거(값도 함께 해제) */
  onRemove: () => void;
}

/**
 * date 입력의 표시값 — 저장은 UTC ISO라 로컬 datetime-local 형식(yyyy-MM-ddTHH:mm:ss)으로
 * 되돌려 보여 준다. `formatDateTimeISO`는 `parseLocalToUTC`와 **같은 기본 시간대**를 쓰므로
 * 왕복이 어긋나지 않는다. 고른 순간 보관한 원문(storedLabel)이 있으면 그대로(입력 중 커서가
 * 튀지 않는다) — 단 날짜만인 구 라벨(yyyy-MM-dd)은 datetime-local이 못 읽으므로 값에서 재합성.
 */
function dateInputValue(current: unknown, storedLabel: string | undefined): string {
  if (current === undefined) return '';
  if (storedLabel && storedLabel.includes('T')) return storedLabel;
  const d = new Date(String(current));
  return Number.isNaN(d.getTime()) ? '' : formatDateTimeISO(d);
}

export function SimulationAxisRow({
  axis,
  current,
  options: loadedOptions,
  storedLabel,
  loading = false,
  error,
  disabled = false,
  radioNamePrefix,
  onSelectOption,
  onTypeValue,
  onRemove,
}: Props) {
  const options =
    loadedOptions ??
    (current !== undefined && storedLabel
      ? [{ label: storedLabel, value: current }]
      : undefined);
  return (
    <div className="flex items-start gap-1.5">
      <span className="mt-1 shrink-0 basis-[120px] text-[11px] font-semibold text-gray-600">
        {axis.label}
      </span>
      <div className="flex-1 min-w-0">
        {axis.widget === 'radio' && (
          <div className="flex flex-wrap items-center gap-2">
            {(options ?? []).map((opt) => (
              <label
                key={String(opt.value)}
                className="flex items-center gap-1 text-xs cursor-pointer"
              >
                <input
                  type="radio"
                  name={`${radioNamePrefix}-${axis.key}`}
                  checked={current === opt.value}
                  disabled={disabled}
                  onChange={() => onSelectOption(opt)}
                  className="h-3 w-3 shrink-0"
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        )}
        {axis.widget === 'select' && (
          <select
            aria-label={axis.label}
            className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:bg-gray-100 disabled:text-gray-400"
            disabled={disabled || loading}
            // 값은 **인덱스**로 오간다 — opaque 값은 문자열로 표현할 수 없고,
            // 표현하려 드는 순간 이 컴포넌트가 그 형상을 알게 된다(§③).
            // 값 일치 우선 → 라벨 폴백 2단계는 `resolveSelectIndex`가 진다.
            value={String(resolveSelectIndex(options, current, storedLabel))}
            onChange={(e) => {
              const idx = Number(e.target.value);
              onSelectOption(idx >= 0 ? options?.[idx] : undefined);
            }}
          >
            <option value="-1">{loading ? '불러오는 중…' : axis.unsetLabel}</option>
            {(options ?? []).map((opt, idx) => (
              <option key={`${opt.label}-${idx}`} value={String(idx)}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
        {(axis.widget === 'text' || axis.widget === 'number' || axis.widget === 'date') && (
          <input
            // date 축은 시각까지 받는다(datetime-local) — 「첫 세션 교육 시작/완료 일시」처럼
            // 시각이 뜻을 갖는 필드가 있고 템플릿도 HH:mm를 그린다(2026-09-03 사용자 지적).
            type={
              axis.widget === 'date'
                ? 'datetime-local'
                : axis.widget === 'text'
                  ? 'text'
                  : axis.widget
            }
            aria-label={axis.label}
            className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
            placeholder={axis.unsetLabel}
            disabled={disabled}
            value={
              axis.widget === 'date'
                ? dateInputValue(current, storedLabel)
                : current === undefined
                  ? ''
                  : String(current)
            }
            onChange={(e) => onTypeValue(e.target.value)}
          />
        )}
        {axis.widget === 'checkbox' && (
          <label className="flex items-center gap-1 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={current === true}
              disabled={disabled}
              onChange={(e) =>
                onSelectOption(
                  e.target.checked ? { label: axis.label, value: true } : undefined,
                )
              }
              className="h-3 w-3 shrink-0"
            />
            <span>{axis.label}</span>
          </label>
        )}
        {/* 선택지가 비었는데 로딩도 아니면 조회가 아무것도 못 찾은 것 — 조용히 빈 칸을
            두면 저작자가 자기 데이터를 의심한다. */}
        {!loading && !error && options && options.length === 0 && (
          <span className="text-[11px] text-gray-400">고를 수 있는 값이 없습니다.</span>
        )}
        {error && <span className="text-[11px] text-red-600">{error}</span>}
        {current === undefined && !loading && !error && axis.widget !== 'checkbox' && (
          <span className="mt-0.5 block text-[10px] text-gray-400">
            값을 고르지 않으면 {axis.unsetLabel}입니다.
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="mt-0.5 shrink-0 rounded px-1 text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
        aria-label={`${axis.label} 모사 해제`}
      >
        ✕
      </button>
    </div>
  );
}
